import React from "react";
import { Check, Cpu, Loader2, Sparkles } from "lucide-react";
import { t } from "@/i18n";

const DEFAULT_STEPS = [
  { name: "parse_profiles", who: "ai" },
  { name: "build_parties", who: "code" },
  { name: "affinity_matrix", who: "both" },
  { name: "grouping_suggestions", who: "ai" },
  { name: "placement", who: "code" },
  { name: "justifications", who: "ai" },
];

/** The server names steps by machine key; the badge says who did the work. */
const whoOf = (step) => {
  if (step.who === "ai" || step.who === "code" || step.who === "both") return step.who;
  const raw = String(step.who || "").toLowerCase();
  if (raw.includes("+")) return "both";
  if (raw === "code") return "code";
  return "ai";
};

const WhoBadge = ({ who }) => {
  const isCode = who === "code";
  const Icon = isCode ? Cpu : Sparkles;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
      style={{
        borderColor: isCode ? "rgba(126,155,139,0.5)" : "rgba(199,164,107,0.6)",
        color: isCode ? "var(--sage-dark)" : "#9a7a3f",
        backgroundColor: isCode ? "#f2f6f3" : "#fdf8ef",
      }}
    >
      <Icon className="h-3 w-3" />
      {t(`timeline.${who}`)}
    </span>
  );
};

export const GenerationTimeline = ({
  steps = DEFAULT_STEPS,
  doneSteps = [],
  currentStep = null,
  elapsed = null,
  status = "idle",
  compact = false,
}) => {
  const doneMap = {};
  doneSteps.forEach((step) => {
    doneMap[step.name] = step.detail;
  });

  return (
    <div data-testid="generation-timeline" className="w-full">
      <ol className="space-y-2.5">
        {steps.map((step, index) => {
          const done = Boolean(doneMap[step.name]);
          const running = status === "running" && currentStep === step.name;
          return (
            <li
              key={step.name}
              data-testid={`timeline-step-${step.name}`}
              data-state={done ? "done" : running ? "running" : "idle"}
              className="flex items-start gap-3"
            >
              <span
                className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-[11px] font-semibold"
                style={{
                  borderColor: done ? "var(--sage-dark)" : running ? "var(--gild)" : "var(--line)",
                  backgroundColor: done ? "#eef4f0" : running ? "#fdf6ea" : "#ffffff",
                  color: done ? "var(--sage-dark)" : running ? "#9a7a3f" : "var(--muted-ink)",
                }}
              >
                {done ? (
                  <Check className="h-3.5 w-3.5" />
                ) : running ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  index + 1
                )}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className="text-sm font-medium"
                    style={{ color: done || running ? "var(--ink)" : "var(--muted-ink)" }}
                  >
                    {t(`step.${step.name}`, step.label)}
                  </span>
                  <WhoBadge who={whoOf(step)} />
                </div>
                {!compact && doneMap[step.name] ? (
                  <p className="mt-0.5 text-xs text-[color:var(--ink-soft)]">{doneMap[step.name]}</p>
                ) : null}
              </div>
            </li>
          );
        })}
      </ol>
      {elapsed !== null && status !== "idle" ? (
        <p className="font-num mt-4 text-xs text-[color:var(--muted-ink)]" data-testid="generation-elapsed">
          {status === "done" ? t("timeline.finishedIn") : t("timeline.elapsed")} {elapsed}s
        </p>
      ) : null}
    </div>
  );
};

export default GenerationTimeline;
